import type { FormikValues } from 'formik';
import { useEffect } from 'react';
import * as Yup from 'yup';

import Auth from '../../../../api/auth';
import Provision from '../../../../api/provision';
import { FormModal } from '../../../../components/Formik';
import useApiQuery from '../../../../hooks/useApiQuery';
import rbacCheck from '../../../../rbac/rbacCheck';

import { useUserData } from '@/redux/selectors';
import type { APIHookArg, ApiResponse } from '@/types';
import type { FormikFormField } from '@/types/formTypes';
import type { Product } from '@/types/productTypes';
import type { Project } from '@/types/projectTypes';

interface EditProductFormModalProps {
  product: Product;
  onSubmit: () => void;
  onCancel: () => void;
}

const EditProductFormModal = ({
  product,
  onSubmit,
  onCancel,
}: EditProductFormModalProps): React.JSX.Element | null => {
  const userData = useUserData();

  const { makeApiCall: updateProductCall } = useApiQuery(
    Auth.updateV2Product as unknown as APIHookArg,
    {
      successMessage: 'productUpdated',
    }
  );
  const {
    data: projects = null,
    makeApiCall: getProjectsCall,
    error: projectsError,
  } = useApiQuery(
    Auth.fetchV2Projects as unknown as (args: unknown) => Promise<ApiResponse<Project>>
  );
  const {
    data: juiceTags = null,
    makeApiCall: getTagsCall,
    error: tagsError,
  } = useApiQuery<string[]>(Provision.fetchTags);

  const canEditTags = rbacCheck(userData.roles, '/products:PUT:TAGS') as boolean;
  const fetchError = projectsError ?? tagsError;

  useEffect(() => {
    getTagsCall();
    getProjectsCall({ organizationId: product.organizationId });
  }, [product.organizationId, getTagsCall, getProjectsCall]);

  const fields = [
    {
      fieldType: 'text',
      name: 'description',
      label: 'Description',
      menuItems: [],
    },
    {
      fieldType: 'select',
      name: 'projectId',
      label: 'Project',
      menuItems: projects
        ? projects.docs?.map(project => ({
            value: project._id ?? '',
            label: project.name,
          }))
        : [],
    },
    ...(canEditTags
      ? [
          {
            fieldType: 'tags',
            name: 'tags',
            label: 'Juice Group Tags',
            menuItems: [],
            tags: juiceTags,
          },
        ]
      : []),
  ] as FormikFormField[];

  const initialValues = {
    description: product.description || '',
    projectId: product.projectId ?? '',
    tags: product.details.tags,
  };

  const validationSchema = Yup.object().shape({
    description: Yup.string().required('Required'),
    projectId: Yup.string().required('Required'),
  });

  const handleSubmit = async (payload: FormikValues): Promise<void> => {
    onCancel();

    const data = await updateProductCall({
      ...payload,
      id: product._id,
    });

    if (data) onSubmit();
  };

  return projects ? (
    <FormModal
      title={`Edit product ${product.name}`}
      fields={fields}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={value => {
        handleSubmit(value);
      }}
      onCancel={onCancel}
      submitLabel='Save'
      error={fetchError}
    />
  ) : null;
};

export default EditProductFormModal;
